/**
 * Email Partner Units — who to write to, and writing to them.
 *
 * The page is built around one comparison: when a unit last wrote to us, beside when we
 * last wrote to them. Either date alone is misleading. "Silent for a year" is the
 * Coverage report's answer and says nothing about whether anybody has asked; "written to
 * last week" says nothing about whether it worked. Together they name the units actually
 * worth a letter today, which is what the two threshold filters select.
 *
 * SENDING IS A LOOP, deliberately visible. The Worker queues every recipient first and
 * then sends a chunk per request — see the head of src/worker/communication.ts for why
 * four hundred letters cannot go in one. This page drives that loop and shows the
 * progress, so a send that stalls is a number that stops rather than a spinner that lies.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../App'
import { api, useApi } from '../lib/api'
import {
  Card, Tile, Badge, Field, Modal, Notice, Spinner, Empty, QueryState, SearchInput, Filters, FilterActions,
  AlumniToggle, CountryLabel, useToast, fmtInt, relDays, downloadCsv,
} from '../components/ui'
import { EnumSelect } from '../components/Select'
import { DataTable } from '../components/DataTable'
import { useRelabel, useCode } from '../lib/terms'

/** The days-ago thresholds offered, as options rather than a number to type. */
const SILENT = [
  { key: '', label: 'Any' },
  { key: '90', label: 'Over 90 days' },
  { key: '365', label: 'Over a year' },
  { key: '730', label: 'Over two years' },
]
const CHASED = [
  { key: '', label: 'Any' },
  { key: '30', label: 'Not in 30 days' },
  { key: '90', label: 'Not in 90 days' },
  { key: '365', label: 'Not in a year' },
]

export default function EmailPartnerUnits({ user }) {
  const t = useRelabel()
  const code = useCode()
  const toast = useToast()
  /* Typed value updates the box immediately; `q`, what actually goes to the Worker, follows
     it a beat later so a fast typist does not fire a request per letter. */
  const [qInput, setQInput] = useState('')
  const [q, setQ] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => setQ(qInput.trim()), 350)
    return () => clearTimeout(timer)
  }, [qInput])
  const [silent, setSilent] = useState('')
  const [chased, setChased] = useState('')
  const [only, setOnly] = useState('with_email')
  const [withAlumni, setWithAlumni] = useState(false)
  /* Partner units to begin with: the letters this page sends are written to partners, in
     the organisation's name, so the other two kinds are opt-in rather than opt-out. The
     Worker applies the same default, so a request that forgets this is narrow and not
     wide — see partnerList. */
  const [kind, setKind] = useState('staff')
  const [picked, setPicked] = useState(() => new Set())
  const [composeOpen, setComposeOpen] = useState(false)
  const [templateId, setTemplateId] = useState('')
  /* null until a send starts, then the running tally the loop below updates. */
  const [progress, setProgress] = useState(null)
  /* Least recently written to first — the question this page is opened with. Held here and
     sent to the Worker rather than sorted in the browser, as everywhere else in the
     archive: the list is capped, so ordering it here would only sort the rows that
     happened to arrive. */
  const [sort, setSort] = useState('last_contacted')
  const [dir, setDir] = useState('asc')

  const allowed = user.caps.manageSettings
  const query = useApi(
    ['comm-partners', q, silent, chased, only, withAlumni, kind, sort, dir],
    `communications/partners?${new URLSearchParams({
      ...(q ? { q } : {}),
      ...(silent ? { silent_days: silent } : {}),
      ...(chased ? { uncontacted_days: chased } : {}),
      ...(only ? { only } : {}),
      ...(withAlumni ? { include_alumni: '1' } : {}),
      fund_type: kind,
      sort,
      dir,
    })}`,
    { enabled: allowed, keepPreviousData: true }
  )
  const templates = useApi('email-templates', 'email-templates', { enabled: allowed })

  if (!allowed) {
    return (
      <PageHeader title={t('Email Partner Units')}>
        <Notice tone="warn" icon="lock" title="Not available to your role">
          This page reads the {t('partner units')}&rsquo; email addresses and sends mail in the
          organization&rsquo;s name, so it is kept to owners and administrators. You are signed
          in as <strong>{user.roleLabel}</strong>.
        </Notice>
      </PageHeader>
    )
  }

  const rows = query.data?.rows ?? []
  const sendable = rows.filter((r) => r.email)

  /* Filters measured against the page's defaults — has an address, partner units only —
     so the count and Clear filters mean "changed from how the page opens". */
  const activeFilters = [silent, chased, only !== 'with_email', withAlumni, kind !== 'staff'].filter(Boolean).length
  const clearFilters = () => {
    setSilent('')
    setChased('')
    setOnly('with_email')
    setWithAlumni(false)
    setKind('staff')
  }
  const exportCsv = () =>
    downloadCsv('partner-units-contact.csv', rows, [
      { key: 's4ca_code', label: 'S4CA Code' },
      { key: 'display_name', label: 'Partner Unit' },
      { key: 'country_name', label: 'Country' },
      { key: 'email', label: 'Email' },
      { key: 'last_newsletter', label: 'Last update' },
      { key: 'last_contacted', label: 'Last written to' },
      { key: 'times_contacted', label: 'Letters sent' },
    ])
  const chosen = rows.filter((r) => picked.has(r.partner_id))
  const chosenSendable = chosen.filter((r) => r.email)

  const toggle = (id) =>
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const allShown = sendable.length > 0 && sendable.every((r) => picked.has(r.partner_id))
  const toggleAll = () =>
    setPicked((prev) => {
      const next = new Set(prev)
      for (const r of sendable) {
        if (allShown) next.delete(r.partner_id)
        else next.add(r.partner_id)
      }
      return next
    })

  /* First click sorts by that column at its useful end — most letters, longest silence —
     and clicking the column already sorted reverses it. The same rule the Updates list
     follows, and the defaults match the Worker's so the first click agrees with what the
     server would have chosen anyway. */
  const SORT_DIR = {
    last_newsletter: 'asc',
    last_contacted: 'asc',
    times_contacted: 'desc',
    newsletter_count: 'desc',
  }
  const onSort = (key) => {
    if (sort === key) setDir((d) => (d === 'desc' ? 'asc' : 'desc'))
    else {
      setSort(key)
      setDir(SORT_DIR[key] ?? 'asc')
    }
  }

  /* What the sub-heading says the order is. Written out rather than assembled from the
     column label and a direction, because "Last written to, ascending" is not English and
     the useful end of each column is not the same end. */
  const SORT_SUB = {
    last_contacted: { asc: 'Least recently written to first', desc: 'Most recently written to first' },
    last_newsletter: { asc: 'Longest silent first', desc: 'Most recently heard from first' },
    times_contacted: { desc: 'Most letters sent first', asc: 'Fewest letters sent first' },
    newsletter_count: { desc: 'Most updates first', asc: 'Fewest updates first' },
    s4ca_code: { asc: 'By code', desc: 'By code, reversed' },
    display_name: { asc: 'By name', desc: 'By name, reversed' },
    country_name: { asc: 'By country', desc: 'By country, reversed' },
    email: { asc: 'By address', desc: 'By address, reversed' },
  }

  const columns = [
    {
      key: 'pick',
      label: '',
      width: 36,
      render: (r) => (
        <input
          type="checkbox"
          checked={picked.has(r.partner_id)}
          disabled={!r.email}
          onChange={() => toggle(r.partner_id)}
          title={r.email ? 'Include in the next send' : 'No address on file'}
        />
      ),
    },
    {
      key: 'unit',
      label: 'Partner unit',
      sortKey: 'display_name',
      render: (r) => (
        <span className="small">
          <Link to={`/partners/${r.partner_id}`} className="mono">
            {code(r.s4ca_code)}
          </Link>{' '}
          {code(r.display_name)}
          {r.status === 'alumni' && (
            <>
              {' '}
              <Badge tone="muted">alumni</Badge>
            </>
          )}
        </span>
      ),
    },
    {
      /* The code is in the column above as well; this one exists to sort by it, and it is
         the identifier the office reads first. Held to one line — a code broken across two
         is not a code. */
      key: 'code',
      label: 'Code',
      sortKey: 's4ca_code',
      width: 110,
      render: (r) => (
        <span className="small mono subtle" style={{ whiteSpace: 'nowrap' }}>
          {code(r.s4ca_code)}
        </span>
      ),
    },
    {
      key: 'country',
      label: 'Country',
      sortKey: 'country_name',
      render: (r) => <CountryLabel name={r.country_name} flag={r.flag_emoji} />,
    },
    {
      key: 'email',
      label: 'Email',
      sortKey: 'email',
      render: (r) =>
        r.email ? <span className="small subtle">{r.email}</span> : <Badge tone="warn">none on file</Badge>,
    },
    {
      key: 'last_update',
      label: 'Last update',
      sortKey: 'last_newsletter',
      render: (r) => (
        <span className="small subtle">{r.last_newsletter ? relDays(r.last_newsletter) : 'never'}</span>
      ),
    },
    {
      key: 'updates',
      label: 'Updates',
      sortKey: 'newsletter_count',
      render: (r) => <span className="num">{fmtInt(r.newsletter_count)}</span>,
    },
    {
      key: 'last_written',
      label: 'Last written to',
      sortKey: 'last_contacted',
      render: (r) => (
        <span className="small subtle">{r.last_contacted ? relDays(r.last_contacted) : 'never'}</span>
      ),
    },
    {
      key: 'letters',
      label: 'Letters',
      sortKey: 'times_contacted',
      render: (r) => <span className="num">{fmtInt(r.times_contacted)}</span>,
    },
  ]

  const chosenTemplate = templates.data?.rows?.find((x) => String(x.id) === String(templateId))

  /**
   * Queue the send, then work through it a chunk at a time until nothing is left.
   *
   * The loop is here rather than in the Worker because each chunk is its own request —
   * that is the only shape that fits inside a Worker's limits for four hundred letters —
   * and because a loop on this side can show what it has done so far. A guard on the
   * iteration count so a Worker that stopped answering cannot spin here for ever.
   */
  const send = async () => {
    setProgress({ sent: 0, failed: 0, total: chosenSendable.length, stage: 'queueing' })
    try {
      const queued = await api('communications', {
        method: 'POST',
        body: { template_id: chosenTemplate?.id, partner_ids: chosenSendable.map((r) => r.partner_id) },
      })
      if (queued.no_email?.length) {
        toast.info(
          `${fmtInt(queued.no_email.length)} of those has no address on file and was not written to`
        )
      }
      let guard = 0
      let res = { remaining: queued.queued, totals: { sent: 0, failed: 0 } }
      setProgress({ sent: 0, failed: 0, total: queued.queued, stage: 'sending' })
      while (res.remaining > 0 && guard++ < 400) {
        res = await api(`communications/${queued.batch_id}/send`, { method: 'POST' })
        setProgress({
          sent: res.totals.sent,
          failed: res.totals.failed,
          total: res.totals.sent + res.totals.failed + res.totals.pending,
          stage: res.done ? 'done' : 'sending',
        })
      }
      setPicked(new Set())
      setComposeOpen(false)
      query.refetch()
      if (res.totals.failed) {
        toast.info(`${fmtInt(res.totals.sent)} sent, ${fmtInt(res.totals.failed)} failed — see the log`)
      } else {
        toast.ok(`${fmtInt(res.totals.sent)} letter${res.totals.sent === 1 ? '' : 's'} sent`)
      }
    } catch (err) {
      toast.error(err)
    } finally {
      setProgress(null)
    }
  }

  return (
    <PageHeader
      title={t('Email Partner Units')}
      sub={t('Ask a partner unit for an update, and see who has been asked')}
      actions={
        <>
          <button className="btn btn-sm" onClick={exportCsv} disabled={!rows.length}>
            ↓ CSV
          </button>
          {chosenSendable.length ? (
            <button
              className="btn btn-sm btn-primary"
              disabled={!!progress}
              onClick={() => setComposeOpen(true)}
            >
              Write to {fmtInt(chosenSendable.length)}
            </button>
          ) : null}
        </>
      }
    >
      <QueryState query={query}>
        {(d) => (
          <div className="col">
            {!d.mailConfigured && (
              <Notice tone="danger" icon="warning" title="Email is not configured">
                Nothing can be sent until the <code>send_email</code> binding and{' '}
                <code>MAIL_FROM</code> are set in <code>wrangler.jsonc</code> and the Worker
                redeployed. The list below still reads.
              </Notice>
            )}

            <div className="grid grid-tiles">
              <Tile
                label={t('Partner units with an address')}
                value={`${fmtInt(d.totals.with_email)} of ${fmtInt(d.totals.units)}`}
                note={`${fmtInt(d.totals.units - d.totals.with_email)} have none — they come from the registry import`}
              />
              <Tile label="Ever written to" value={fmtInt(d.totals.ever_contacted)} />
              <Tile label="Letters sent" value={fmtInt(d.totals.sent)} />
              <Tile
                label="Failed"
                value={fmtInt(d.totals.failed)}
                tone={d.totals.failed ? 'danger' : undefined}
                to={d.totals.failed ? '/communication/log?status=failed' : undefined}
              />
            </div>

            {/* The same controls every list carries: the search box on its own line, then
                the Filters panel, then the list. */}
            <SearchInput
              id="epu-q"
              value={qInput}
              onChange={setQInput}
              placeholder={t('Code, name or address…')}
              aria-label="Search partner units"
            />
            <Filters id="email-partner-units" active={activeFilters} onClear={clearFilters}>
              <Field label="Has not written" htmlFor="epu-silent">
                <EnumSelect
                  inputId="epu-silent"
                  value={silent || null}
                  onChange={(v) => setSilent(v ?? '')}
                  options={SILENT.filter((o) => o.key).map((o) => ({ value: o.key, label: o.label }))}
                />
              </Field>
              <Field label="Has not been written to" htmlFor="epu-chased">
                <EnumSelect
                  inputId="epu-chased"
                  value={chased || null}
                  onChange={(v) => setChased(v ?? '')}
                  options={CHASED.filter((o) => o.key).map((o) => ({ value: o.key, label: o.label }))}
                />
              </Field>
              <Field label="Address" htmlFor="epu-only">
                <EnumSelect
                  inputId="epu-only"
                  value={only || null}
                  onChange={(v) => setOnly(v ?? '')}
                  placeholder="Either"
                  options={[
                    { value: 'with_email', label: 'Has one' },
                    { value: 'without_email', label: 'Has none' },
                  ]}
                />
              </Field>
              <Field label="Record kind" hint="Partner units unless you widen it" htmlFor="epu-kind">
                <EnumSelect
                  inputId="epu-kind"
                  value={kind}
                  onChange={(v) => setKind(v ?? 'staff')}
                  isClearable={false}
                  options={[
                    { value: 'staff', label: 'Partner units' },
                    { value: 'training', label: 'Training schools' },
                    { value: 'project', label: 'Projects' },
                    { value: 'all', label: 'All three' },
                  ]}
                />
              </Field>
              <AlumniToggle checked={withAlumni} onChange={setWithAlumni} />
              <FilterActions active={activeFilters} onClear={clearFilters} busy={query.isFetching} />
            </Filters>

            <div className="row wrap">
              <button className="btn btn-sm" onClick={toggleAll} disabled={!sendable.length}>
                {allShown ? 'Clear selection' : `Select all ${fmtInt(sendable.length)} shown`}
              </button>
              {!!picked.size && (
                <span className="small subtle">
                  {fmtInt(picked.size)} selected
                  {picked.size !== chosenSendable.length &&
                    ` — ${fmtInt(picked.size - chosenSendable.length)} without an address will be skipped`}
                </span>
              )}
            </div>

            {!rows.length ? (
              <Empty title="Nothing to show">
                {t('No partner unit matches those filters.')}
              </Empty>
            ) : (
              <Card
                title={`${fmtInt(rows.length)} ${t(rows.length === 1 ? 'Partner Unit' : 'Partner Units')}`}
                sub={SORT_SUB[sort]?.[dir] ?? 'Ordered by the column you chose'}
              >
                {/* The archive's own sortable table, so the headers, the arrows and the
                    bar of sort buttons that replaces them on a narrow screen are the same
                    ones every other list here has. The checkbox column carries no sortKey
                    and so is not sortable, which is right: a selection is not an order. */}
                <DataTable
                  columns={columns}
                  rows={rows}
                  sort={sort}
                  dir={dir}
                  onSort={onSort}
                  rowKey={(r) => r.partner_id}
                  compact
                />
              </Card>
            )}

            <Modal
              open={composeOpen}
              onClose={() => !progress && setComposeOpen(false)}
              title={`Write to ${fmtInt(chosenSendable.length)} ${t(
                chosenSendable.length === 1 ? 'partner unit' : 'partner units'
              )}`}
              footer={
                <>
                  <button
                    className="btn btn-primary"
                    onClick={send}
                    disabled={!chosenTemplate || !!progress || !d.mailConfigured}
                  >
                    {progress ? <Spinner /> : `Send ${fmtInt(chosenSendable.length)}`}
                  </button>
                  <button className="btn" onClick={() => setComposeOpen(false)} disabled={!!progress}>
                    Cancel
                  </button>
                </>
              }
            >
              <div className="col">
                <Field label="Letter" hint="Written and edited on the Email Templates page." htmlFor="em-letter">
                  <EnumSelect
                    inputId="em-letter"
                    value={templateId}
                    onChange={(v) => setTemplateId(v == null ? '' : String(v))}
                    options={(templates.data?.rows ?? []).map((x) => ({
                      value: String(x.id),
                      label: `${x.name}${x.is_default ? ' (default)' : ''}`,
                    }))}
                    placeholder="Choose a letter…"
                    isDisabled={!!progress}
                  />
                </Field>

                {chosenTemplate && (
                  <Card title="As the First Recipient Will Read It" sub={chosenSendable[0]?.email}>
                    <p className="small" style={{ margin: '0 0 8px', fontWeight: 600 }}>
                      {fill(chosenTemplate.subject, chosenSendable[0], code, user.orgName)}
                    </p>
                    <pre
                      className="small"
                      style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}
                    >
                      {fill(chosenTemplate.body, chosenSendable[0], code, user.orgName)}
                    </pre>
                  </Card>
                )}

                {progress && (
                  <Notice icon="mail" title={progress.stage === 'done' ? 'Sent' : 'Sending…'}>
                    {fmtInt(progress.sent)} sent
                    {progress.failed ? `, ${fmtInt(progress.failed)} failed` : ''} of{' '}
                    {fmtInt(progress.total)}. Each letter is recorded as it goes, so closing this
                    does not lose what has already been sent.
                  </Notice>
                )}

                <p className="small subtle" style={{ margin: 0 }}>
                  Every letter is recorded against the {t('partner unit')} it went to, with who
                  sent it — the Communication Log has all of it. Anything that bounces is kept as
                  a failure rather than deleted: “we tried and it bounced” is not the same as “we
                  never wrote”.
                </p>
              </div>
            </Modal>
          </div>
        )}
      </QueryState>
    </PageHeader>
  )
}

/**
 * A preview of the letter for one recipient.
 *
 * The Worker fills the real thing; this is the same substitution done here so the preview
 * needs no round trip. Every placeholder the Worker resolves is resolved here too,
 * including the organisation's name — which /api/me already carries. An earlier draft left
 * `{{org}}` standing on the grounds that the name was the server's to supply, and the
 * result was a preview showing a letter that would go out looking different from the one
 * on screen. A preview that is not the letter is worse than no preview.
 */
function fill(text, unit, code, org) {
  if (!unit) return text
  const values = {
    code: code(unit.s4ca_code),
    name: unit.display_name || code(unit.s4ca_code),
    country: unit.country_name || 'your country',
    last_update: unit.last_newsletter || 'a while ago',
    org,
  }
  return String(text ?? '').replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (whole, key) => {
    const v = values[key.toLowerCase()]
    // Left standing when unknown or empty, exactly as the Worker does it.
    return v === undefined || v === null || v === '' ? whole : String(v)
  })
}
